import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LIST_FILTER_KEYS } from "constants/listFilters";
import {
  getStoredFilters,
  parseIdListParam,
  saveStoredFilters,
  serializeIdList,
} from "services/listFilterStorage";

function matchStoredItems(storedItems = [], ids = []) {
  if (!ids.length) return [];
  const idSet = new Set(ids);
  return storedItems.filter((item) => idSet.has(item?._id));
}

function useUsersSchoolCityFilters() {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;

  const hasHydratedRef = useRef(false);

  const [selectedUniversitiesData, setSelectedUniversitiesData] = useState([]);
  const [selectedUniversitiesIdsData, setSelectedUniversitiesIdsData] = useState([]);
  const [selectedUniversities, setSelectedUniversities] = useState([]);
  const [selectedUniversitiesIds, setSelectedUniversitiesIds] = useState([]);
  const [selectedCitiesData, setSelectedCitiesData] = useState([]);
  const [selectedCitiesIdsData, setSelectedCitiesIdsData] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedCitiesIds, setSelectedCitiesIds] = useState([]);

  const navigateWithParams = useCallback(
    (params) => {
      const { pathname } = locationRef.current;
      const query = params.toString();
      navigate(query ? `${pathname}?${query}` : pathname, { replace: true });
    },
    [navigate]
  );

  const persistFilters = useCallback((schools, schoolIds, cities, cityIds, { resetPageToFirst = false } = {}) => {
    const params = new URLSearchParams(locationRef.current.search);

    if (schoolIds.length > 0) {
      params.set("schools", serializeIdList(schoolIds));
    } else {
      params.delete("schools");
    }

    if (cityIds.length > 0) {
      params.set("cities", serializeIdList(cityIds));
    } else {
      params.delete("cities");
    }

    if (resetPageToFirst) {
      params.delete("page");
    }

    saveStoredFilters(LIST_FILTER_KEYS.USERS, {
      schools,
      cities,
      ...(resetPageToFirst ? { page: 1 } : {}),
    });

    navigateWithParams(params);

    setSelectedUniversitiesData(schools);
    setSelectedUniversitiesIdsData(schoolIds);
    setSelectedUniversities(schools);
    setSelectedUniversitiesIds(schoolIds);
    setSelectedCitiesData(cities);
    setSelectedCitiesIdsData(cityIds);
    setSelectedCities(cities);
    setSelectedCitiesIds(cityIds);
  }, [navigateWithParams]);

  useEffect(() => {
    if (hasHydratedRef.current) return;

    const timeoutId = setTimeout(() => {
      if (hasHydratedRef.current) return;

      const params = new URLSearchParams(locationRef.current.search);
      const stored = getStoredFilters(LIST_FILTER_KEYS.USERS);
      let changed = false;

      let schoolIds = parseIdListParam(params.get("schools"));
      let cityIds = parseIdListParam(params.get("cities"));
      let schools = matchStoredItems(stored.schools, schoolIds);
      let cities = matchStoredItems(stored.cities, cityIds);

      if (schoolIds.length === 0 && Array.isArray(stored.schools) && stored.schools.length > 0) {
        schools = stored.schools;
        schoolIds = schools.map((school) => school._id).filter(Boolean);
        params.set("schools", serializeIdList(schoolIds));
        changed = true;
      }

      if (cityIds.length === 0 && Array.isArray(stored.cities) && stored.cities.length > 0) {
        cities = stored.cities;
        cityIds = cities.map((city) => city._id).filter(Boolean);
        params.set("cities", serializeIdList(cityIds));
        changed = true;
      }

      hasHydratedRef.current = true;

      setSelectedUniversitiesData(schools);
      setSelectedUniversitiesIdsData(schoolIds);
      setSelectedUniversities(schools);
      setSelectedUniversitiesIds(schoolIds);
      setSelectedCitiesData(cities);
      setSelectedCitiesIdsData(cityIds);
      setSelectedCities(cities);
      setSelectedCitiesIds(cityIds);

      saveStoredFilters(LIST_FILTER_KEYS.USERS, { schools, cities });

      if (changed) {
        navigateWithParams(params);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [navigateWithParams]);

  const applySchoolCityFilters = useCallback(
    (schools, schoolIds, cities, cityIds) => {
      persistFilters(schools, schoolIds, cities, cityIds, { resetPageToFirst: true });
    },
    [persistFilters]
  );

  const removeSchool = useCallback(
    (schoolId) => {
      const schools = selectedUniversitiesData.filter((item) => item?._id !== schoolId);
      const schoolIds = selectedUniversitiesIdsData.filter((id) => id !== schoolId);
      persistFilters(schools, schoolIds, selectedCitiesData, selectedCitiesIdsData, {
        resetPageToFirst: true,
      });
    },
    [
      persistFilters,
      selectedCitiesData,
      selectedCitiesIdsData,
      selectedUniversitiesData,
      selectedUniversitiesIdsData,
    ]
  );

  const removeCity = useCallback(
    (cityId) => {
      const cities = selectedCitiesData.filter((item) => item?._id !== cityId);
      const cityIds = selectedCitiesIdsData.filter((id) => id !== cityId);
      persistFilters(selectedUniversitiesData, selectedUniversitiesIdsData, cities, cityIds, {
        resetPageToFirst: true,
      });
    },
    [
      persistFilters,
      selectedCitiesData,
      selectedCitiesIdsData,
      selectedUniversitiesData,
      selectedUniversitiesIdsData,
    ]
  );

  return {
    selectedUniversitiesData,
    selectedUniversitiesIdsData,
    selectedUniversities,
    setSelectedUniversities,
    selectedUniversitiesIds,
    setSelectedUniversitiesIds,
    selectedCitiesData,
    selectedCitiesIdsData,
    selectedCities,
    setSelectedCities,
    selectedCitiesIds,
    setSelectedCitiesIds,
    applySchoolCityFilters,
    removeSchool,
    removeCity,
  };
}

export default useUsersSchoolCityFilters;
